import { useRouter, isNavigationFailure, NavigationFailureType, type RouteLocationRaw } from 'vue-router'
import { toast } from 'vue-sonner'
import { useOrganizationStore } from '@/stores/organization'

// Switching organization is a tenant change: every mounted page holds data
// fetched under the old X-Organization-Id. Both app shells key their
// authenticated <router-view> on the org id, so the write below remounts the
// whole tree and every component-bound composable refetches on its own.
//
// ⚠ The ORDER is the whole composable. Writing the store id first would let
// Vue's flush remount the CURRENT route (a job page) under the new tenant
// before the router has moved — a Silent Wall 404 and an error toast — then a
// second mount at the destination. Writing it after the navigation settles
// mounts the destination twice, the first time under the old org. `afterEach`
// runs synchronously in the same turn the router commits the new route, so the
// id lands in the SAME flush: the destination mounts exactly once, under the
// new organization.
//
// The destination defaults to home: a record URL belongs to the old org and
// cannot be read under the new one. A push deep link names its own tenant AND
// its record, so it passes the record's path. Always `replace`: the page being
// left is unreachable under the new tenant, so « back » must not return to it.
// Entries further back in history are still the old org's and will read as
// 404s — a known limitation, accepted.
export function useOrganizationSwitch() {
  const orgStore = useOrganizationStore()
  const router = useRouter()

  const switchOrganization = (id: string, to: RouteLocationRaw = { name: 'home' }) => new Promise<void>((resolve) => {
    const off = router.afterEach((_to, _from, failure) => {
      off()
      // Already there ⇒ a duplicated navigation, which still reaches here.
      if (!failure || isNavigationFailure(failure, NavigationFailureType.duplicated)) {
        orgStore.setCurrentOrganization(id)
        // An « Ouvrir » toast still open points at an old-org record.
        toast.dismiss()
      }
      resolve()
    })
    void router.replace(to)
  })

  return { switchOrganization }
}
